import { ClientPostStatsSortingValues, ClientTiktokTopVideo, getTrndsClientAudioPostStatsById } from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import useAbortableEffect from '../../../../../../../Hooks/useAbortableEffect';
import { showNotification } from '../../../../../../../helpers';
import { OrderByParam } from '../../../../../../../Hooks/useReactTableSortToOrderBy';
import { convertOrderByToOrdering } from '../../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable.helpers';

const isValidOrderingValue = (value: string): value is ClientPostStatsSortingValues => {
    const validValues = [
        'create_time',
        'play_count',
        'like_count',
        'share_count',
        'comment_count',
        'save_count',
        'author_follower_count',
    ];
    return !!validValues.find((validValue) => validValue === value || `-${validValue}` === value);
};

type useClientTopPostsDataParams = Pick<
    Parameters<typeof getTrndsClientAudioPostStatsById>[0],
    'page' | 'page_size'
> & {
    clientId: number | undefined;
    audioId: number | undefined;
} & OrderByParam<ClientTiktokTopVideo>;

export function useClientTopPostsData({
    audioId,
    clientId,
    page,
    page_size,
    order_by_asc,
    order_by_desc,
}: useClientTopPostsDataParams) {
    const [postStats, setPostStats] = useState<ClientTiktokTopVideo[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const ordering = useMemo(
        () =>
            order_by_asc || order_by_desc
                ? (convertOrderByToOrdering({ order_by_asc, order_by_desc }) as ClientPostStatsSortingValues)
                : undefined,
        [order_by_asc, order_by_desc]
    );

    const fetchData = useCallback(async (...params: Parameters<typeof getTrndsClientAudioPostStatsById>) => {
        try {
            setIsLoading(true);
            setHasError(false);
            const response = await getTrndsClientAudioPostStatsById(...params);

            if (response.status === 200) {
                setPostStats(response.data.results);
                setHasNextPage(Boolean(response.data.next));
                setCount(response.data.count);
                return;
            }
            showNotification(`couldn't fetch top posts`, 'error');
            setHasError(true);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification(`couldn't fetch top posts`, 'error');
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (!clientId || !audioId || (ordering && !isValidOrderingValue(ordering))) {
                return;
            }
            fetchData({ page, page_size, clientId, audioId, ordering }, { signal });
        },
        [fetchData, page, page_size, clientId, audioId, ordering]
    );

    return useMemo(
        () => ({
            postStats,
            hasNextPage,
            count,
            isLoading,
            hasError,
        }),
        [postStats, hasNextPage, count, isLoading, hasError]
    );
}
